import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../api';
import { Button, Label, TextInput, Card, Alert, Table, Pagination, Select, Checkbox } from 'flowbite-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import ConfirmModal from '../components/ConfirmModal';

export default function Properties() {
  const { appUuid: routeAppUuid } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [appUuid, setAppUuid] = useState(routeAppUuid || '');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [confirm, setConfirm] = useState({ open: false, onConfirm: null, title: '', message: '' });

  const [filters, setFilters] = useState({
    search: '',
    city: '',
    deal_type: '',
    property_type_ext: '',
    category_ext: '',
    min_price: '',
    max_price: '',
    min_area: '',
    max_area: '',
    min_rooms: '',
    max_rooms: '',
    new_building: false,
    mortgage: false,
    sort: 'created_desc',
  });

  const [importUrl, setImportUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  const CATEGORY_OPTIONS = {
    residential: [
      { value: 'apartment', label: 'Квартира' },
      { value: 'room', label: 'Комната' },
      { value: 'studio', label: 'Студия' },
    ],
    country: [
      { value: 'house', label: 'Дом' },
      { value: 'cottage', label: 'Коттедж' },
      { value: 'townhouse', label: 'Таунхаус' },
      { value: 'land-plot', label: 'Земельный участок' },
    ],
    commercial: [
      { value: 'office', label: 'Офис' },
      { value: 'retail', label: 'Ритейл' },
      { value: 'warehouse', label: 'Склад' },
      { value: 'free-purpose', label: 'Свободного назначения' },
    ],
  };

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (appUuid) params.set('app_uuid', appUuid);
      if (filters.search) params.set('search', filters.search);
      if (filters.city) params.set('city', filters.city);
      if (filters.deal_type) params.set('deal_type', filters.deal_type);
      if (filters.property_type_ext) params.set('property_type_ext', filters.property_type_ext);
      if (filters.category_ext) params.set('category_ext', filters.category_ext);
      if (filters.min_price !== '') params.set('min_price', String(filters.min_price));
      if (filters.max_price !== '') params.set('max_price', String(filters.max_price));
      if (filters.min_area !== '') params.set('min_area', String(filters.min_area));
      if (filters.max_area !== '') params.set('max_area', String(filters.max_area));
      if (filters.min_rooms !== '') params.set('min_rooms', String(filters.min_rooms));
      if (filters.max_rooms !== '') params.set('max_rooms', String(filters.max_rooms));
      if (filters.new_building) params.set('new_building', 'true');
      if (filters.mortgage) params.set('mortgage', 'true');
      if (filters.sort) params.set('sort', filters.sort);
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      const qs = `?${params.toString()}`;
      const data = await api.get(`/properties${qs}`);
      setItems(data.items || []);
      if (typeof data.total === 'number') setTotal(data.total);
      if (typeof data.page_size === 'number') setPageSize(data.page_size);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { setAppUuid(routeAppUuid || ''); }, [routeAppUuid]);
  const loadCb = useCallback(load, [appUuid, page, pageSize, filters]);
  useEffect(() => { loadCb(); }, [loadCb]);

  const applyFilter = async (e) => {
    e.preventDefault();
    setPage(1);
    await load();
  };
  const resetFilter = async () => {
    setFilters({
      search: '', city: '', deal_type: '', property_type_ext: '', category_ext: '',
      min_price: '', max_price: '', min_area: '', max_area: '', min_rooms: '', max_rooms: '',
      new_building: false, mortgage: false, sort: 'created_desc',
    });
    setPage(1);
  };
  const openCreate = () => navigate(`/${routeAppUuid}/properties/new`);
  const openEdit = (id) => navigate(`/${routeAppUuid}/properties/${id}/edit`);

  const onImportByUrl = async () => {
    if (!importUrl) return;
    try {
      setImportLoading(true);
      const body = { app_uuid: routeAppUuid || appUuid, url: importUrl, download_images: true };
      const res = await api.post(`/import-by-url`, body);
      const id = res?.item?.id;
      if (id) {
        addToast({ type: 'success', message: 'Импорт завершён' });
        navigate(`/${routeAppUuid || appUuid}/properties/${id}/edit`);
      } else {
        addToast({ type: 'warning', message: 'Импорт выполнен, но объект не найден в ответе' });
      }
    } catch (e) {
      addToast({ type: 'failure', message: e.message || 'Ошибка импорта' });
    } finally {
      setImportLoading(false);
    }
  };

  const remove = async (id) => {
    setConfirm({
      open: true,
      title: 'Удалить объект?',
      message: 'Вы уверены, что хотите удалить этот объект?',
      onConfirm: async () => {
        setConfirm({ open: false, title: '', message: '', onConfirm: null });
        try {
          await api.delete(`/properties/${id}`);
          addToast({ type: 'success', message: 'Объект удален' });
          await load();
        } catch (e) {
          setError(e.message);
        }
      },
    });
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const pageItems = useMemo(() => items, [items]);

  const onChangeFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const onChangePropertyType = (value) => {
    const categories = CATEGORY_OPTIONS[value] || [];
    setFilters((f) => ({ ...f, property_type_ext: value, category_ext: categories[0]?.value || '' }));
  };

  return (
    <div className="grid gap-4">
      <ConfirmModal open={confirm.open} title={confirm.title} message={confirm.message} onClose={() => setConfirm({ open: false })} onConfirm={confirm.onConfirm} />
      <Card>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Объекты</h2>
          <div className="flex gap-2 items-center">
            <div className="hidden sm:flex items-center gap-2">
              <TextInput style={{ width: 320 }} placeholder="Ссылка на объявление" value={importUrl} onChange={(e) => setImportUrl(e.target.value)} />
              <Button color="purple" onClick={onImportByUrl} disabled={!importUrl || importLoading}>{importLoading ? 'Импорт…' : 'Импорт (GPT)'}</Button>
            </div>
            <Select value={filters.sort} onChange={(e) => onChangeFilter('sort', e.target.value)}>
              <option value="created_desc">Новые сначала</option>
              <option value="created_asc">Старые сначала</option>
              <option value="price_desc">Цена ↓</option>
              <option value="price_asc">Цена ↑</option>
              <option value="area_desc">Площадь ↓</option>
              <option value="area_asc">Площадь ↑</option>
              <option value="views_desc">Просмотры ↓</option>
              <option value="views_asc">Просмотры ↑</option>
              <option value="title_asc">Название A→Z</option>
              <option value="title_desc">Название Z→A</option>
            </Select>
            <Button onClick={openCreate}>Добавить</Button>
          </div>
        </div>
        <div className="sm:hidden grid gap-2">
          <div className="flex gap-2">
            <TextInput placeholder="Ссылка на объявление" value={importUrl} onChange={(e) => setImportUrl(e.target.value)} />
            <Button color="purple" onClick={onImportByUrl} disabled={!importUrl || importLoading}>{importLoading ? 'Импорт…' : 'Импорт (GPT)'}</Button>
          </div>
        </div>
        {error && <Alert color="failure" className="mt-2">{error}</Alert>}
        {!routeAppUuid && (
          <form onSubmit={applyFilter} className="flex items-end gap-2 mb-3 mt-2">
            <div className="flex-1">
              <Label htmlFor="app_uuid" value="ID приложения (app_uuid)" />
              <TextInput id="app_uuid" placeholder="UUID приложения" value={appUuid} onChange={e => setAppUuid(e.target.value)} />
            </div>
            <Button type="submit">Фильтровать</Button>
          </form>
        )}

        <form onSubmit={applyFilter} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mt-3">
          <div>
            <Label value="Поиск" />
            <TextInput placeholder="название/описание/город" value={filters.search} onChange={(e) => onChangeFilter('search', e.target.value)} />
          </div>
          <div>
            <Label value="Город" />
            <TextInput placeholder="Город" value={filters.city} onChange={(e) => onChangeFilter('city', e.target.value)} />
          </div>
          <div>
            <Label value="Тип сделки" />
            <Select value={filters.deal_type} onChange={(e) => onChangeFilter('deal_type', e.target.value)}>
              <option value="">Любой</option>
              <option value="sale">Продажа</option>
              <option value="rent">Аренда</option>
            </Select>
          </div>
          <div>
            <Label value="Тип недвижимости" />
            <Select value={filters.property_type_ext} onChange={(e) => onChangePropertyType(e.target.value)}>
              <option value="">Любой</option>
              <option value="residential">Жилая</option>
              <option value="country">Загородная</option>
              <option value="commercial">Коммерческая</option>
            </Select>
          </div>
          <div>
            <Label value="Категория" />
            <Select value={filters.category_ext} onChange={(e) => onChangeFilter('category_ext', e.target.value)}>
              <option value="">Любая</option>
              {(CATEGORY_OPTIONS[filters.property_type_ext] || []).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label value="Цена от" />
            <TextInput type="number" placeholder="Мин" value={filters.min_price} onChange={(e) => onChangeFilter('min_price', e.target.value)} />
          </div>
          <div>
            <Label value="Цена до" />
            <TextInput type="number" placeholder="Макс" value={filters.max_price} onChange={(e) => onChangeFilter('max_price', e.target.value)} />
          </div>
          <div>
            <Label value="Площадь от" />
            <TextInput type="number" placeholder="Мин" value={filters.min_area} onChange={(e) => onChangeFilter('min_area', e.target.value)} />
          </div>
          <div>
            <Label value="Площадь до" />
            <TextInput type="number" placeholder="Макс" value={filters.max_area} onChange={(e) => onChangeFilter('max_area', e.target.value)} />
          </div>
          <div>
            <Label value="Комнат от" />
            <TextInput type="number" placeholder="Мин" value={filters.min_rooms} onChange={(e) => onChangeFilter('min_rooms', e.target.value)} />
          </div>
          <div>
            <Label value="Комнат до" />
            <TextInput type="number" placeholder="Макс" value={filters.max_rooms} onChange={(e) => onChangeFilter('max_rooms', e.target.value)} />
          </div>
          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center gap-2">
              <Checkbox checked={filters.new_building} onChange={(e) => onChangeFilter('new_building', e.target.checked)} />
              <Label value="Новостройка" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={filters.mortgage} onChange={(e) => onChangeFilter('mortgage', e.target.checked)} />
              <Label value="Ипотека" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit">Применить</Button>
            <Button color="light" type="button" onClick={resetFilter}>Сбросить</Button>
          </div>
        </form>

        {/* Mobile cards */}
        <div className="grid gap-3 sm:hidden mt-3">
          {pageItems.length === 0 ? (
            <div className="text-sm text-gray-500">Объекты не найдены</div>
          ) : (
            pageItems.map(p => (
              <Card key={p.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold break-words">{p.title}</div>
                    <div className="text-xs text-gray-500 mt-1">{p.city}</div>
                  </div>
                  <div className="text-right text-sm whitespace-nowrap">
                    <div>{p.price} {p.currency}</div>
                    <div className="text-gray-500">{p.area} м²</div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Просмотры: <button className="text-blue-600 hover:underline" onClick={() => navigate(`/${routeAppUuid}/properties/${p.id}/stats`)}> {p.views} </button>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button size="xs" color="light" onClick={() => openEdit(p.id)}>Редактировать</Button>
                  <Button size="xs" color="failure" onClick={() => remove(p.id)}>Удалить</Button>
                </div>
              </Card>
            ))
          )}
        </div>
        {/* Desktop table */}
        <div className="overflow-x-auto hidden sm:block mt-3">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Название</Table.HeadCell>
              <Table.HeadCell className="hidden md:table-cell">Город</Table.HeadCell>
              <Table.HeadCell className="hidden lg:table-cell">Цена</Table.HeadCell>
              <Table.HeadCell className="hidden lg:table-cell">Площадь</Table.HeadCell>
              <Table.HeadCell className="hidden xl:table-cell">Просмотры</Table.HeadCell>
              <Table.HeadCell>
                <span className="sr-only">Действия</span>
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {pageItems.map((p) => (
                <Table.Row key={p.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                  <Table.Cell className="max-w-[220px] sm:max-w-none break-words">
                    {p.title}
                    <div className="mt-1 text-xs text-gray-500 sm:hidden">{p.city}</div>
                  </Table.Cell>
                  <Table.Cell className="hidden md:table-cell">{p.city}</Table.Cell>
                  <Table.Cell className="hidden lg:table-cell whitespace-nowrap">{p.price} {p.currency}</Table.Cell>
                  <Table.Cell className="hidden lg:table-cell whitespace-nowrap">{p.area}</Table.Cell>
                  <Table.Cell className="hidden xl:table-cell whitespace-nowrap">
                    <button className="text-blue-600 hover:underline" onClick={() => navigate(`/${routeAppUuid}/properties/${p.id}/stats`)} title="Открыть статистику">
                      {p.views}
                    </button>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button size="xs" color="light" onClick={() => openEdit(p.id)}>Редактировать</Button>
                      <Button size="xs" color="failure" onClick={() => remove(p.id)}>Удалить</Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
              {pageItems.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6} className="text-center text-sm text-gray-500">Объекты не найдены</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm text-gray-500">Всего: {total}</div>
          <Pagination currentPage={page} onPageChange={setPage} totalPages={totalPages} showIcons />
        </div>
      </Card>
    </div>
  );
}